/**
 * QuestionContent Component
 *
 * Handles the question content with form field and conditional controls
 */

import { IFormField } from '@ViewModels';
import { ISelectBoxOption } from '@WebComponents/SelectBox';
import { css } from 'aphrodite';
import * as React from 'react';
import { FormFieldWithOptions } from '../../../FormFieldWithOptions';
import { QuestionType } from '../../../FormFieldWithOptions/questionTypes';
import { styleSheet } from '../../styles';
import { ConditionalQuestionControlsProps, ConditionalVisibilityProps } from '../../types';
import { ConditionalQuestionControls } from '../ConditionalQuestionControls';
import { ConditionalVisibilityControls } from '../ConditionalVisibilityControls';

interface QuestionContentProps {
	field: IFormField<string>;
	questionIndex: number;
	onToggleOptional: (index: number) => void;
	onLabelChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
	onRenderTriggerTitle: (selectedOption: ISelectBoxOption<QuestionType>) => React.ReactNode;
	onFieldTypeChange: (selectedType: ISelectBoxOption<QuestionType>, index: number) => void;
	onFieldUpdate: (field: IFormField<string>, index: number) => void;
	isEdit: boolean;
	onAfterItem?: (optionLabel: string) => React.ReactNode;
	conditionalQuestionProps?: ConditionalQuestionControlsProps;
	conditionalVisibilityProps?: ConditionalVisibilityProps;
	formFieldOptions?: Array<{ title: string; value: QuestionType }>;
	/** Optional extra content rendered inside the question card after the field options. */
	children?: React.ReactNode;
	/**
	 * Optional pre-resolved RHF errors for this question. Forwarded to FormFieldWithOptions
	 * so the V2 sectioned editor can surface per-question validation without relying on the
	 * V1 `customForm.fields.[index]` error path.
	 */
	questionErrors?: {
		label?: { message?: string };
		options?: { message?: string };
	};
	/**
	 * Whether this question is already persisted to the BE. Forwarded to
	 * FormFieldWithOptions so the V2 editor can disable the type-selector
	 * only for saved questions (V1 callers can omit; the inner heuristic
	 * still works for `customForm.fields[]`).
	 */
	hasBeenSaved?: boolean;
}

export const QuestionContent: React.FC<QuestionContentProps> = React.memo(
	({
		field,
		questionIndex,
		onToggleOptional,
		onLabelChange,
		onRenderTriggerTitle,
		onFieldTypeChange,
		onFieldUpdate,
		isEdit,
		onAfterItem,
		conditionalQuestionProps,
		conditionalVisibilityProps,
		formFieldOptions,
		children,
		questionErrors,
		hasBeenSaved,
	}) => {
		return (
			<div
				className={css(
					styleSheet.section,
					styleSheet.sectionFullWidth,
					styleSheet.sectionNoBorderTop,
					styleSheet.borderRightBottom
				)}
			>
				<FormFieldWithOptions
					field={field}
					questionIndex={questionIndex}
					value={field.label}
					onToggleCheckChanged={onToggleOptional}
					onChange={onLabelChange}
					onRenderTriggerTitle={onRenderTriggerTitle}
					handleFormFieldTypeChange={onFieldTypeChange}
					onUpdateField={onFieldUpdate}
					isEdit={isEdit}
					onAfterItem={onAfterItem}
					formFieldOptions={formFieldOptions}
					questionErrors={questionErrors}
					hasBeenSaved={hasBeenSaved}
				>
					{conditionalQuestionProps && <ConditionalQuestionControls {...conditionalQuestionProps} />}
					{conditionalVisibilityProps && <ConditionalVisibilityControls {...conditionalVisibilityProps} />}
					{children}
				</FormFieldWithOptions>
			</div>
		);
	}
);

QuestionContent.displayName = 'QuestionContent';
